// frontend/src/components/TaxonomyQualityWidget.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { focusRing, hoverOnly } from '../theme/tokens';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KpiRow, { KpiCell } from './common/KpiRow';
import StatTile from './common/StatTile';
import { scrollIntoViewRespectingMotion } from '../utils/scroll';
import { formatPercent } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const withApiBase = (path) => `${API_BASE}${path}`;

// `overall_score` u. a. sind 0..100 (`backend/docs/TAXONOMY_QUALITY_SETUP.md`),
// `formatPercent` nimmt einen Bruch — daher die Division.
const pct = (x) => formatPercent(Number(x || 0) / 100, { digits: 1 });

// Dispatch a SKU subset to the products table. ProductsTable listens for
// `portfolio-dashboard:apply-filter`; an empty list is a no-op so the user
// never lands on an empty table.
const emitTaxonomyFilter = (skus, id) => {
  const arr = Array.from(new Set((skus || []).filter(Boolean)));
  if (!arr.length) return;
  window.dispatchEvent(
    new CustomEvent('portfolio-dashboard:apply-filter', {
      detail: { skus: arr, key: `taxonomy:${id}` },
    })
  );
  const anchor = document.getElementById('products-table-anchor');
  if (anchor) scrollIntoViewRespectingMotion(anchor, { block: 'start' });
};

export default function TaxonomyQualityWidget({ compact = false }) {
  const [kpis, setKpis] = useState(null);
  const [issues, setIssues] = useState([]);
  const [, setLoading] = useState(true);

  // SKUs of the top issue + the union across all loaded (critical) issues.
  const topIssue = issues[0] || null;
  const topIssueSkus = topIssue?.skus || [];
  const criticalSkus = Array.from(
    new Set(issues.flatMap((i) => i?.skus || []).filter(Boolean))
  );
  // The "missing taxonomy records" issue feeds the "Missing" chip.
  const withoutTaxonomyIssue = issues.find(
    (i) => i?.category === 'completeness' && /missing taxonomy records/i.test(i?.message || '')
  );
  const withoutTaxonomySkus = withoutTaxonomyIssue?.skus || [];

  // Shared sx for clickable stat affordance (cursor + hover + keyboard focus).
  const clickableSx = {
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    borderRadius: 1,
    ...hoverOnly({ opacity: 0.7 }),
    '&:focus-visible': focusRing(),
  };

  // Enter / Space activate a click handler on a non-button element (Space also
  // prevents page scroll). Returns a keydown handler bound to the given fn.
  const keyActivate = (fn) => (e) => {
    if (!fn) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      fn(e);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch KPIs
      const kpisResponse = await axios.get(withApiBase('/api/dashboard/taxonomy-quality/kpis'));
      setKpis(kpisResponse.data);

      // Fetch critical issues
      const issuesResponse = await axios.get(
        withApiBase('/api/dashboard/taxonomy-quality/issues?severity=critical&limit=5')
      );
      setIssues(issuesResponse.data.issues || []);
    } catch (err) {
      console.error('Error fetching taxonomy quality data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (compact) {
    // Compact version for home dashboard
    return (
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Taxonomy Quality</Typography>
          <Tooltip
            title="Monitors completeness, consistency, and freshness of product taxonomy data."
            arrow
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={fetchData}>
            Refresh
          </Button>
        </Stack>

        <KpiRow>
          <KpiCell>
            <StatTile
              label="Overall score"
              value={pct(kpis?.overall_score ?? 0)}
              labelTooltip="Weighted average of completeness, consistency, and freshness."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label="Completeness"
              value={pct(kpis?.completeness_score ?? 0)}
              labelTooltip="% of products with all required taxonomy fields populated."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label="Consistency"
              value={pct(kpis?.consistency_score ?? 0)}
              labelTooltip="% of products with logically coherent taxonomy data."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label="Freshness"
              value={pct(kpis?.freshness_score ?? 0)}
              labelTooltip="% of products with taxonomy data updated within 90 days."
            />
          </KpiCell>
        </KpiRow>

        {(kpis?.critical_issues > 0 || kpis?.warnings > 0 || kpis?.info > 0) && (
          <KpiRow>
            <KpiCell>
              <Typography variant="caption" color="text.secondary">
                Issues
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {kpis?.critical_issues > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.critical_issues} Critical`}
                    color="error"
                  />
                )}
                {kpis?.warnings > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.warnings} Warnings`}
                    color="warning"
                  />
                )}
                {kpis?.info > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.info} Info`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </KpiCell>
          </KpiRow>
        )}
      </Box>
    );
  }

  // Full version for product overview
  return (
    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6">Taxonomy Quality</Typography>
        <Tooltip
          title="Monitors completeness, consistency, and freshness of product taxonomy data."
          arrow
        >
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={fetchData}>
          Refresh
        </Button>
      </Stack>

      <KpiRow>
        <KpiCell>
          <StatTile
            label="Overall score"
            value={pct(kpis?.overall_score ?? 0)}
            labelTooltip="Weighted average: 50% completeness, 30% consistency, 20% freshness."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Completeness"
            value={pct(kpis?.completeness_score ?? 0)}
            labelTooltip="Are all required fields (canonical_type, role, etc.) populated?"
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Consistency"
            value={pct(kpis?.consistency_score ?? 0)}
            labelTooltip="Is the data logically coherent? (e.g., grinders have grind_range)"
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Freshness"
            value={pct(kpis?.freshness_score ?? 0)}
            labelTooltip="Is taxonomy data up-to-date? (updated within 90 days)"
          />
        </KpiCell>
      </KpiRow>

      <KpiRow sx={{ mt: 2 }}>
        <KpiCell>
          <Typography variant="caption" color="text.secondary">
            Products
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`${kpis?.total_products ?? 0} Total`}
              variant="outlined"
            />
            {kpis?.with_taxonomy > 0 && (
              <Chip
                size="small"
                label={`${kpis.with_taxonomy} With taxonomy`}
                color="success"
              />
            )}
            {kpis?.without_taxonomy > 0 && (
              <Chip
                size="small"
                label={`${kpis.without_taxonomy} Missing`}
                color="error"
                onClick={
                  withoutTaxonomySkus.length
                    ? () => emitTaxonomyFilter(withoutTaxonomySkus, 'without_taxonomy')
                    : undefined
                }
                aria-label={
                  withoutTaxonomySkus.length
                    ? `Filter products missing taxonomy records (${kpis.without_taxonomy})`
                    : undefined
                }
                sx={withoutTaxonomySkus.length ? clickableSx : undefined}
              />
            )}
          </Stack>
        </KpiCell>

        <KpiCell>
          <Typography variant="caption" color="text.secondary">
            Issues
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {kpis?.critical_issues === 0 && kpis?.warnings === 0 ? (
              <Chip
                size="small"
                label="No critical issues"
                color="success"
              />
            ) : (
              <>
                {kpis?.critical_issues > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.critical_issues} Critical`}
                    color="error"
                    onClick={
                      criticalSkus.length
                        ? () => emitTaxonomyFilter(criticalSkus, 'critical')
                        : undefined
                    }
                    aria-label={
                      criticalSkus.length
                        ? `Filter products with critical taxonomy issues (${kpis.critical_issues})`
                        : undefined
                    }
                    sx={criticalSkus.length ? clickableSx : undefined}
                  />
                )}
                {kpis?.warnings > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.warnings} Warnings`}
                    color="warning"
                  />
                )}
                {kpis?.info > 0 && (
                  <Chip
                    size="small"
                    label={`${kpis.info} Info`}
                    variant="outlined"
                  />
                )}
              </>
            )}
          </Stack>
        </KpiCell>

        {issues.length > 0 && (
          <KpiCell>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              onClick={
                topIssueSkus.length
                  ? () => emitTaxonomyFilter(topIssueSkus, topIssue?.category || 'top')
                  : undefined
              }
              {...(topIssueSkus.length && {
                role: 'button',
                tabIndex: 0,
                'aria-label': `Filter products by issue: ${topIssue.message}`,
                onKeyDown: keyActivate(() =>
                  emitTaxonomyFilter(topIssueSkus, topIssue?.category || 'top')
                ),
              })}
              sx={topIssueSkus.length ? clickableSx : undefined}
            >
              <WarningAmberIcon color="error" fontSize="small" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Top issue: {topIssue.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {topIssue.count} products affected
                  {topIssueSkus.length ? ' · click to filter' : ''}
                </Typography>
              </Box>
            </Stack>
          </KpiCell>
        )}
      </KpiRow>
    </Box>
  );
}
